import type {
	ComboBoxFilterChangeEvent,
	MultiSelectChangeEvent,
} from "@tql/ui-components-react/kendo/dropdowns";
import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { IdName } from "~/shared/schemas/commodity-schema.ts";
import type { Customer } from "~/shared/schemas/customer-schema.ts";
import { customerSchema } from "~/shared/schemas/customer-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";
import {
	availableCustomersAtom,
	selectedCustomerAtom,
} from "~/shared/state/user-profile-state.ts";

type UseCustomerSelectionOptions = {
	isInternal: boolean;
};

export const useCustomerSelection = ({
	isInternal,
}: UseCustomerSelectionOptions) => {
	const [selectedCustomerGlobal] = useAtom(selectedCustomerAtom);
	const [availableCustomers] = useAtom(availableCustomersAtom);

	const [isSearching, setIsSearching] = useState(false);
	const [foundCustomers, setFoundCustomers] = useState<Customer[]>([]);
	const hasUserCleared = useRef(false);

	const externalCustomers = useMemo<IdName[]>(
		() =>
			availableCustomers.map((customer) => ({
				id: customer.id,
				name: customer.name,
			})),
		[availableCustomers],
	);

	const [selectedCustomers, setSelectedCustomers] = useState<IdName[]>(() => {
		if (isInternal) {
			const storedCustomers =
				globalThis.sessionStorage.getItem("selectedCustomers");
			if (storedCustomers) {
				try {
					return JSON.parse(storedCustomers) as IdName[];
				} catch (error) {
					console.error("Failed to parse stored customers:", error);
				}
			}
			return [];
		}
		if (selectedCustomerGlobal?.id && selectedCustomerGlobal?.name) {
			return [
				{
					id: selectedCustomerGlobal.id,
					name: selectedCustomerGlobal.name,
				},
			];
		}
		return [];
	});

	// Persist internal user's selection to session storage
	useEffect(() => {
		if (isInternal) {
			globalThis.sessionStorage.setItem(
				"selectedCustomers",
				JSON.stringify(selectedCustomers),
			);
		}
	}, [selectedCustomers, isInternal]);

	// Sync external customers when available customers or global selection changes
	useEffect(() => {
		if (!isInternal) {
			const validIds = new Set(
				externalCustomers.map((customer) => customer.id),
			);
			setSelectedCustomers((previousCustomers) => {
				const validPreviousCustomers = previousCustomers.filter((customer) =>
					validIds.has(customer.id),
				);

				if (validPreviousCustomers.length > 0) {
					return validPreviousCustomers;
				}

				// User explicitly cleared the selection — don't fall back
				if (hasUserCleared.current) {
					return [];
				}

				if (
					selectedCustomerGlobal?.id &&
					selectedCustomerGlobal?.name &&
					validIds.has(selectedCustomerGlobal.id)
				) {
					return [
						{
							id: selectedCustomerGlobal.id,
							name: selectedCustomerGlobal.name,
						},
					];
				}

				const [firstExternalCustomer] = externalCustomers;
				if (firstExternalCustomer) {
					return [firstExternalCustomer];
				}

				return [];
			});
		}
	}, [
		externalCustomers,
		isInternal,
		selectedCustomerGlobal?.id,
		selectedCustomerGlobal?.name,
	]);

	const handleChange = useCallback(
		(e: MultiSelectChangeEvent) => {
			const selectedItems = e.value as IdName[];
			const isSelectAllSelected = selectedItems.some(
				(item) => item.id === -1 || item.name === "Select All",
			);

			if (!isInternal && isSelectAllSelected) {
				hasUserCleared.current = false;
				setSelectedCustomers(externalCustomers);
				return;
			}

			hasUserCleared.current = selectedItems.length === 0;
			setSelectedCustomers(selectedItems);
		},
		[externalCustomers, isInternal],
	);

	const handleFilterChange = useCallback((e: ComboBoxFilterChangeEvent) => {
		const searchTerm = (e.filter.value as string) || "";
		if (searchTerm.length < 3) {
			return;
		}
		setFoundCustomers([]);
		setIsSearching(true);
		void getAndValidate(
			apiRoutes.CUSTOMERSEARCH.replace(
				":userInput",
				encodeURIComponent(String(e.filter.value)),
			),
			customerSchema.array(),
		)
			.then((results) => {
				if (results.success && Array.isArray(results.data)) {
					setFoundCustomers(results.data);
				}
			})
			.finally(() => {
				setIsSearching(false);
			});
	}, []);

	return {
		selectedCustomers,
		externalCustomers,
		foundCustomers,
		isSearching,
		handleChange,
		handleFilterChange,
	};
};
